import React from "react";
import { Instagram, Facebook, Twitter, Youtube } from "lucide-react";
import { LogoMark } from "./Logo";
import { useLang } from "../../i18n/LanguageProvider";
import { AGENCY } from "../../i18n/dict";

const social = [
  { name: "Instagram", Icon: Instagram, href: "https://instagram.com" },
  { name: "Facebook", Icon: Facebook, href: "https://facebook.com" },
  { name: "Twitter", Icon: Twitter, href: "https://twitter.com" },
  { name: "Youtube", Icon: Youtube, href: "https://youtube.com" },
];

export const Footer = () => {
  const { t } = useLang();

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0B1416] text-[#F5F1E8] border-t border-[#F5F1E8]/10"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <LogoMark size={44} tone="light" />
              <div className="font-heading text-3xl">Travel To Time</div>
            </div>
            <p className="mt-6 max-w-md text-base font-light opacity-70 leading-relaxed">
              {t.footer.tagline}
            </p>
            <div className="mt-8 flex items-center gap-3">
              {social.map(({ name, Icon, href }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`social-${name.toLowerCase()}`}
                  aria-label={name}
                  className="w-10 h-10 border border-[#F5F1E8]/20 rounded-full flex items-center justify-center hover:bg-[#1BB6B9] hover:text-[#0B1416] hover:border-[#1BB6B9] transition-colors"
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="overline text-[#6EE3E6] mb-5">{t.footer.contact}</div>
            <div className="space-y-3 text-sm font-light">
              <a href={`tel:${AGENCY.phone_raw}`} className="block hover:text-[#6EE3E6] transition-colors">
                {AGENCY.phone}
              </a>
              <a href={`mailto:${AGENCY.email}`} className="block hover:text-[#6EE3E6] transition-colors">
                {AGENCY.email}
              </a>
              <div className="opacity-70">{AGENCY.website}</div>
              <div className="opacity-70 pt-2">
                {AGENCY.address}
                <br />
                {AGENCY.address2}
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="overline text-[#6EE3E6] mb-5">{t.footer.hours}</div>
            <div className="text-sm font-light opacity-80">
              {t.footer.hours_val}
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="overline text-[#6EE3E6] mb-5">{t.footer.legal}</div>
            <ul className="space-y-3 text-sm font-light">
              <li>
                <a href="#" className="opacity-70 hover:opacity-100 transition-opacity">
                  {t.footer.privacy}
                </a>
              </li>
              <li>
                <a href="#" className="opacity-70 hover:opacity-100 transition-opacity">
                  {t.footer.terms}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#F5F1E8]/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs opacity-60">
          <span>{t.footer.copy}</span>
          <span className="font-italiana tracking-wider">Est. 2011 · Mataró</span>
        </div>

        <div
          data-testid="agency-credit"
          className="mt-6 pt-4 border-t border-[#F5F1E8]/5 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[10px] uppercase tracking-[0.28em] opacity-45 hover:opacity-80 transition-opacity"
        >
          <a
            href="https://www.aiguelweb.online"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#6EE3E6] transition-colors font-medium"
          >
            Aiguel Web Agency
          </a>
          <span className="opacity-50">© 2026</span>
          <span className="opacity-50">·</span>
          <span>Tous droits réservés</span>
          <span className="opacity-50">·</span>
          <a
            href="mailto:hello.aiguel@gmail.com"
            className="hover:text-[#6EE3E6] transition-colors normal-case tracking-normal"
          >
            hello.aiguel@gmail.com
          </a>
          <span className="opacity-50">·</span>
          <a
            href="https://www.aiguelweb.online"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#6EE3E6] transition-colors normal-case tracking-normal"
          >
            www.aiguelweb.online
          </a>
        </div>
      </div>
    </footer>
  );
};
